// Dependencies
import React, { FC, useContext, useState, useEffect, createContext, useRef } from 'react'

import { payments } from '@square/web-sdk'
import type * as Square from '@square/web-sdk'

// Internals
import useEvent from 'Hooks/useEvent'

import ErrorScreen from '../../ErrorScreen'

import type { PaymentCallback, FormContextType, FormProviderProps } from './types'

/**
 * Internal helper that the `PaymentForm` uses to manage internal state and
 * expose access to the Web Payment SDK library.
 */
const FormContext = createContext<FormContextType>({
  cardTokenizeResponseReceived: () => Promise.resolve(),
  createPaymentRequest: {} as Square.PaymentRequestOptions,
  payments: {} as Square.Payments,
  cardTokenizeRef: { current: () => {} },
  giftCardTokenizeRef: { current: () => {} },
})

const FormProvider: FC<FormProviderProps> = ({
  applicationId,
  locationId,
  children,
  overrides,
  createPaymentRequest,
  paymentFormError = () => {},
  paymentFormReady = () => {},
  ...props
}) => {
  const [instance, setInstance] = useState<Square.Payments>()

  const cardTokenizeRef = useRef<PaymentCallback>(() => {})
  const giftCardTokenizeRef = useRef<PaymentCallback>(() => {})

  useEffect(() => {
    const abortController = new AbortController()
    const { signal } = abortController

    async function loadPayment(signal?: AbortSignal): Promise<void> {
      try {
        await payments(applicationId, locationId, overrides).then((res) => {
          if (res === null) {
            throw new Error('Square Web Payments SDK failed to load')
          }

          if (signal?.aborted) {
            return
          }

          setInstance(res)
          paymentFormReady()

          return res
        })
      } catch (error) {
        console.error(error)
        paymentFormError('Unable to init Square payment form')
      }
    }

    if (applicationId && locationId) {
      void loadPayment(signal)
    }

    return () => {
      abortController.abort()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationId, locationId])

  const cardTokenizeResponseReceived = useEvent(async (result: Square.TokenResult): Promise<void> => {
    if (result.status !== 'OK' || !props.createVerificationDetails) {
      await props.cardTokenizeResponseReceived(result)
      return
    }

    const verifyBuyerResults = await instance?.verifyBuyer(String(result.token), props.createVerificationDetails())

    await props.cardTokenizeResponseReceived(result, verifyBuyerResults)
  })

  if (!applicationId || !locationId) {
    return <ErrorScreen />
  }

  if (!instance) return null

  const context: FormContextType = {
    cardTokenizeResponseReceived,
    createPaymentRequest,
    payments: instance,
    cardTokenizeRef,
    giftCardTokenizeRef,
  }

  return <FormContext.Provider value={context}>{children}</FormContext.Provider>
}

const useForm = (): FormContextType => {
  const context = useContext(FormContext)

  if (context === undefined) {
    throw new Error('useForm must be used within a FormProvider')
  }

  return context
}

export { FormContext, useForm }
export default FormProvider
